/**
 * RecoverStrandedButton — one-click "self-heal every story whose raw
 * upload disappeared from cloud storage". Sits in the Studio top bar
 * next to "Translate library" and "Backfill articles".
 *
 * Background: stories uploaded before the persist-on-upload fix lost
 * their raw .mp4 on production container restarts. The backend already
 * lazy-heals these the next time anyone clicks a new language on them,
 * but this button does it proactively for every story at once so the
 * first foreign-language visitor doesn't pay the cost.
 *
 * Wraps the admin endpoint POST /api/admin/recover-stranded-sources,
 * which heals up to N stories per call (rate-limited to dodge upstream
 * proxy timeouts). The button just keeps re-calling it in a tight
 * loop until `still_stranded` hits zero.
 */
import React, { useState } from "react";
import axios from "axios";
import { Wand2, Loader2, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_TOKEN = "storylo-admin"; // matches backend default

export default function RecoverStrandedButton() {
  const [state, setState] = useState("idle"); // idle | running | done | error
  const [progress, setProgress] = useState(null);

  const run = async () => {
    if (!window.confirm(
      "Recover any stories whose raw upload was lost from cloud storage?\n\n" +
      "This is safe to run anytime — it only touches stories that genuinely " +
      "need fixing, and uses the existing rendered video as a stand-in source. " +
      "Takes 1-5 minutes depending on how many need healing."
    )) return;

    setState("running");
    setProgress(null);
    let totalHealed = 0;
    let totalAlreadyOk = 0;
    let totalUnrecoverable = 0;

    try {
      // Cap at 8 rounds; the endpoint heals up to 5 per round so 8 ×
      // 5 = 40 stories is plenty of headroom for the library size we
      // ever expect to grow to.
      for (let round = 1; round <= 8; round++) {
        setProgress({ round, totalHealed, totalAlreadyOk, totalUnrecoverable });
        const { data } = await axios.post(
          `${API}/admin/recover-stranded-sources?limit=5`,
          null,
          {
            headers: { "X-Admin-Token": ADMIN_TOKEN },
            timeout: 90 * 1000,
          },
        );
        totalHealed += data.healed || 0;
        totalAlreadyOk = data.already_ok || totalAlreadyOk;
        totalUnrecoverable += data.unrecoverable || 0;
        if ((data.still_stranded || 0) === 0) break;
      }
      setProgress({
        round: null, totalHealed, totalAlreadyOk, totalUnrecoverable,
      });
      setState("done");
      // Hold the green badge a bit longer so the operator actually
      // notices the result before it resets.
      setTimeout(() => setState("idle"), 8000);
    } catch (e) {
      console.error("recover-stranded error:", e);
      setProgress({
        error: e?.response?.data?.detail || e?.message || "Failed",
      });
      setState("error");
      setTimeout(() => setState("idle"), 6000);
    }
  };

  const label = () => {
    if (state === "running") {
      if (progress?.round) {
        return `Scanning… (round ${progress.round}, ${progress.totalHealed} healed)`;
      }
      return "Scanning library…";
    }
    if (state === "done") {
      if (progress?.totalHealed === 0) {
        return `Already healthy · ${progress.totalAlreadyOk} stories ok`;
      }
      return `Healed ${progress.totalHealed} stor${progress.totalHealed === 1 ? "y" : "ies"}${progress.totalUnrecoverable ? ` · ${progress.totalUnrecoverable} need re-upload` : ""}`;
    }
    if (state === "error") return progress?.error || "Try again";
    return "Recover stranded";
  };

  return (
    <button
      onClick={run}
      disabled={state === "running"}
      className={`text-[11px] uppercase tracking-[0.18em] px-3 py-2 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
        state === "done" ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
        : state === "error" ? "border-red-500/50 text-red-300 bg-red-500/10"
        : "border-white/15 hover:border-[color:var(--accent)]/60 hover:text-[color:var(--accent)]"
      }`}
      data-testid="studio-recover-stranded"
      title="Rebuild the raw upload for any story whose .mp4 was lost from cloud storage. Safe to run anytime."
    >
      {state === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : state === "done" ? <Check className="w-3.5 h-3.5" />
        : state === "error" ? <X className="w-3.5 h-3.5" />
        : <Wand2 className="w-3.5 h-3.5" />}
      <span className="hidden sm:inline">{label()}</span>
    </button>
  );
}
